import { useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

const METHOD_LABELS = {
  upi:  "UPI",
  card: "Debit / Credit Card",
  nb:   "Net Banking",
  cod:  "Cash on Delivery",
};

export default function OrderSuccess() {
  const location = useLocation();
  const navigate  = useNavigate();
  const state     = location.state;

  // ── Security: redirect if accessed directly without state ──
  useEffect(() => {
    if (!state) navigate("/", { replace: true });
  }, [state, navigate]);

  if (!state) return null;

  const { items = [], total = 0, method, orderId, paymentId, isCOD } = state;

  return (
    <div className="tc-page min-h-screen" style={{ animation: "osPageIn 0.4s ease both" }}>
      <style>{`
        @keyframes osPageIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes osCheckDraw {
          from { stroke-dashoffset: 60; opacity: 0; }
          to   { stroke-dashoffset: 0;  opacity: 1; }
        }
        @keyframes osRingPop {
          0%   { transform: scale(0.6); opacity: 0; }
          70%  { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes osGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
          50%       { box-shadow: 0 0 0 12px rgba(16,185,129,0); }
        }
        @keyframes osCardIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .os-cta-primary {
          transition: background 0.18s, transform 0.18s, box-shadow 0.18s;
        }
        .os-cta-primary:hover {
          background: var(--accent-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(16,185,129,0.28);
        }
        .os-cta-secondary {
          transition: border-color 0.18s, color 0.18s;
        }
        .os-cta-secondary:hover {
          border-color: var(--border-default) !important;
          color: var(--text-primary) !important;
        }
      `}</style>

      <div style={{ maxWidth: 520, margin: "0 auto", padding: "48px 20px 80px" }}>

        {/* ── Success icon ── */}
        <div style={{ textAlign: "center", marginBottom: 32, animation: "osCardIn 0.4s ease 0.05s both" }}>
          <div style={{
            width: 80, height: 80,
            borderRadius: "50%",
            background: "var(--accent-bg)",
            border: "2px solid var(--accent)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "osRingPop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s both, osGlow 2.5s ease-in-out 0.6s infinite",
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline
                points="4 12 9 17 20 6"
                style={{
                  strokeDasharray: 60,
                  strokeDashoffset: 0,
                  animation: "osCheckDraw 0.45s ease 0.35s both",
                }}
              />
            </svg>
          </div>

          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(32px, 8vw, 48px)",
            letterSpacing: "0.05em",
            color: "var(--text-primary)",
            lineHeight: 1,
            marginTop: 20,
            marginBottom: 8,
          }}>
            {isCOD ? "ORDER PLACED" : "PAYMENT DONE"}
          </h1>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {isCOD
              ? "Your order has been placed. We'll collect payment on delivery."
              : "Your payment was successful. We're preparing your order."}
          </p>
        </div>

        {/* ── Order summary card ── */}
        <div
          className="tc-card"
          style={{ padding: 0, overflow: "hidden", animation: "osCardIn 0.4s ease 0.15s both", marginBottom: 14 }}
        >
          {/* Header */}
          <div style={{
            padding: "13px 20px",
            borderBottom: "1px solid var(--border-subtle)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              Order Summary
            </span>
            <span style={{ fontSize: 9, fontFamily: "var(--font-mono)", color: "var(--accent)", background: "var(--accent-bg)", border: "1px solid var(--accent)", borderRadius: "var(--radius-pill)", padding: "2px 8px" }}>
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Items */}
          <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((item, i) => (
              <div key={item.id ?? i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--accent)", marginTop: 6, flexShrink: 0, opacity: 0.6 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </p>
                  <p style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 1 }}>
                    qty {item.quantity}
                  </p>
                </div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", flexShrink: 0, fontFamily: "var(--font-mono)" }}>
                  ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                </p>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{ padding: "13px 20px", borderTop: "1px solid var(--border-subtle)", background: "var(--bg-tertiary)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              {isCOD ? "Amount due on delivery" : "Total paid"}
            </span>
            <span style={{ fontFamily: "var(--font-display)", fontSize: 32, letterSpacing: "0.04em", color: "var(--accent)", lineHeight: 1 }}>
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        {/* ── Payment details card ── */}
        <div
          className="tc-card"
          style={{ padding: 0, overflow: "hidden", animation: "osCardIn 0.4s ease 0.22s both", marginBottom: 24 }}
        >
          <div style={{ padding: "13px 20px", borderBottom: "1px solid var(--border-subtle)" }}>
            <span style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              Payment Details
            </span>
          </div>

          <div style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 1, background: "var(--border-subtle)" }}>
            {[
              { label: "Method", value: METHOD_LABELS[method] || method },
              !isCOD && orderId  && { label: "Order ID",   value: orderId },
              !isCOD && paymentId && { label: "Payment ID", value: paymentId },
              { label: "Status", value: isCOD ? "Pending — pay on delivery" : "Confirmed" },
            ].filter(Boolean).map(row => (
              <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "10px 12px", background: "var(--bg-card)" }}>
                <span style={{ fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "var(--font-mono)", flexShrink: 0 }}>
                  {row.label}
                </span>
                <span style={{
                  fontSize: 11,
                  color: row.label === "Status"
                    ? (isCOD ? "var(--warning-text)" : "var(--success-text)")
                    : "var(--text-secondary)",
                  fontFamily: "var(--font-mono)",
                  wordBreak: "break-all",
                  textAlign: "right",
                }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* Estimated delivery */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              Estimated delivery: <span style={{ color: "var(--text-secondary)" }}>3–5 business days</span>
            </span>
          </div>
        </div>

        {/* ── CTAs ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, animation: "osCardIn 0.4s ease 0.28s both" }}>
          <Link
            to="/dashboard"
            className="os-cta-primary"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: "14px 24px",
              borderRadius: "var(--radius-md)",
              background: "var(--accent)",
              color: "var(--bg-primary)",
              textDecoration: "none",
              fontFamily: "var(--font-display)",
              fontSize: 18,
              letterSpacing: "0.06em",
            }}
          >
            VIEW MY ORDERS
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
          </Link>

          <Link
            to="/"
            className="os-cta-secondary"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "11px 24px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              background: "transparent",
              color: "var(--text-muted)",
              textDecoration: "none",
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontFamily: "var(--font-mono)",
            }}
          >
            Continue Shopping
          </Link>
        </div>

      </div>
    </div>
  );
}
